/**
 * Cache keys, queue names and event names.
 *
 * Every cache key and queue name in the platform is constructed here.
 * Ad-hoc key strings scattered across services are the usual cause of cache
 * poisoning and orphaned queues, so the builders below are the only
 * sanctioned way to produce one.
 */
const CACHE_NAMESPACE = 'ibe';
/** Cache key prefixes, namespaced so they stay legible. */
export const CACHE_PREFIX = {
    AVAILABILITY: `${CACHE_NAMESPACE}:availability`,
    PRICING: `${CACHE_NAMESPACE}:pricing`,
    RATE_PLAN: `${CACHE_NAMESPACE}:rate-plan`,
    EXCHANGE_RATE: `${CACHE_NAMESPACE}:exchange-rate`,
    PROPERTY: `${CACHE_NAMESPACE}:property`,
    ROOM_TYPE: `${CACHE_NAMESPACE}:room-type`,
    SESSION: `${CACHE_NAMESPACE}:session`,
    IDEMPOTENCY: `${CACHE_NAMESPACE}:idempotency`,
    RATE_LIMIT: `${CACHE_NAMESPACE}:rate-limit`,
    LOCK: `${CACHE_NAMESPACE}:lock`,
    BUSINESS_RULES: `${CACHE_NAMESPACE}:business-rules`,
    IMPORT: `${CACHE_NAMESPACE}:import`,
};
/** The segment standing in for "no tenant" in a platform-scoped key. */
const PLATFORM_SEGMENT = '_platform';
/**
 * Builds a namespaced, tenant-scoped cache key.
 *
 * The tenant is a required positional argument rather than an optional one.
 * Under multi-tenancy a shared cache key is a cross-tenant read: two tenants
 * sending the same idempotency key would otherwise collide, and the second
 * caller would receive the first's stored response body. Making the tenant
 * required turns that from something a reviewer has to notice into a
 * compile error.
 */
export function cacheKey(prefix, tenantId, ...segments) {
    if (tenantId === '') {
        throw new Error('cacheKey requires a tenant. Use platformCacheKey for global entries.');
    }
    return [prefix, tenantId, ...segments.map(String)].join(':');
}
/**
 * A key that is deliberately shared across tenants.
 *
 * Exchange rates and other genuinely global reference data. Separate from
 * `cacheKey` so that "this is shared" is an explicit, greppable decision
 * rather than an omitted argument.
 */
export function platformCacheKey(prefix, ...segments) {
    return [prefix, PLATFORM_SEGMENT, ...segments.map(String)].join(':');
}
/** Cache lifetimes in seconds. Tuned per category in Task 24. */
export const CACHE_TTL_SECONDS = {
    AVAILABILITY: 60,
    PRICING: 60,
    RATE_PLAN: 300,
    EXCHANGE_RATE: 3600,
    PROPERTY: 600,
    ROOM_TYPE: 600,
    BUSINESS_RULES: 300,
    /** The master prompt fixes idempotency key retention at 24 hours. */
    IDEMPOTENCY: 86_400,
    /**
     * A staged import plan, in seconds — thirty minutes.
     *
     * Long enough to review a few hundred rows without hurrying, short enough
     * that a plan nobody committed does not outlive the data it was planned
     * against by much. The commit re-resolves parents regardless.
     */
    IMPORT_PLAN: 1800,
};
export const QUEUES = {
    RESERVATION_EVENTS: 'ibe.reservation.events',
    PAYMENT_EVENTS: 'ibe.payment.events',
    INTEGRATION_JOBS: 'ibe.integration.jobs',
    INTEGRATION_RETRY: 'ibe.integration.retry',
    INTEGRATION_DEAD_LETTER: 'ibe.integration.dlq',
    NOTIFICATION_EMAIL: 'ibe.notification.email',
    NOTIFICATION_SMS: 'ibe.notification.sms',
    NOTIFICATION_PRIORITY: 'ibe.notification.priority',
    NOTIFICATION_RETRY: 'ibe.notification.retry',
    NOTIFICATION_DEAD_LETTER: 'ibe.notification.dlq',
    AUDIT_WRITE: 'ibe.audit.write',
};
/**
 * Domain event names.
 *
 * SPEC CORRECTION (SPEC_CORRECTIONS.md #9): Task 04 publishes
 * `PasswordResetRequested` and `GuestEmailVerificationRequested` and delegates
 * delivery to Task 20, but Task 20's event-consumption list omitted both —
 * guest registration could never complete. Both are declared here so the
 * contract exists in one place for publisher and consumer alike.
 */
export const EVENTS = {
    // Reservation (Task 12)
    RESERVATION_CREATED: 'ReservationCreated',
    RESERVATION_MODIFIED: 'ReservationModified',
    RESERVATION_CANCELLED: 'ReservationCancelled',
    RESERVATION_EXPIRED: 'ReservationExpired',
    RESERVATION_CONFIRMED: 'ReservationConfirmed',
    RESERVATION_STATUS_CHANGED: 'ReservationStatusChanged',
    RESERVATION_CHECKED_IN: 'ReservationCheckedIn',
    RESERVATION_CHECKED_OUT: 'ReservationCheckedOut',
    // Payment (Task 19)
    PAYMENT_INITIATED: 'PaymentInitiated',
    PAYMENT_AUTHORIZED: 'PaymentAuthorized',
    PAYMENT_CAPTURED: 'PaymentCaptured',
    PAYMENT_PARTIALLY_CAPTURED: 'PaymentPartiallyCaptured',
    PAYMENT_REFUNDED: 'PaymentRefunded',
    PAYMENT_PARTIALLY_REFUNDED: 'PaymentPartiallyRefunded',
    PAYMENT_VOIDED: 'PaymentVoided',
    PAYMENT_FAILED: 'PaymentFailed',
    // Identity (Task 04) — consumed by the Notification Engine (Task 20)
    PASSWORD_RESET_REQUESTED: 'PasswordResetRequested',
    GUEST_EMAIL_VERIFICATION_REQUESTED: 'GuestEmailVerificationRequested',
    GUEST_ACCOUNT_REGISTERED: 'GuestAccountRegistered',
    // Guest rights (Task 09)
    GUEST_RIGHTS_REQUEST_SUBMITTED: 'GuestRightsRequestSubmitted',
    GUEST_RIGHTS_REQUEST_COMPLETED: 'GuestRightsRequestCompleted',
    GUEST_CONSENT_CHANGED: 'GuestConsentChanged',
    // Notification / messaging (Task 20)
    NOTIFICATION_DISPATCH_REQUESTED: 'NotificationDispatchRequested',
    NOTIFICATION_DELIVERY_UPDATED: 'NotificationDeliveryUpdated',
    NOTIFICATION_TEMPLATE_CHANGED: 'NotificationTemplateChanged',
    NOTIFICATION_GATEWAY_CHANGED: 'NotificationGatewayChanged',
    // PMS side-effect retry queue
    PMS_SIDE_EFFECT_REQUESTED: 'PmsSideEffectRequested',
    PMS_SIDE_EFFECT_RETRY: 'PmsSideEffectRetry',
    // Translation (Task 25)
    TRANSLATION_REQUESTED: 'TranslationRequested',
};
//# sourceMappingURL=messaging.js.map